import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import type { ProviderPlaceItem } from '../types/providerPlace';
import {
  getStoredProviderPlaceId,
  setStoredProviderPlaceId,
} from '../utils/providerDashboardPlace';
import { getProviderPlaceDisplayColor } from '../utils/providerPlaceColor';

function getPlaceLabel(place: ProviderPlaceItem): string {
  return place.display_name || place.name;
}

type ProviderPlaceContextValue = {
  providerId: number | null;
  activePlaces: ProviderPlaceItem[];
  selectedPlaceId: number | null;
  selectedPlace: ProviderPlaceItem | null;
  setSelectedPlaceId: (placeId: number) => void;
  hasMultiplePlaces: boolean;
  isReady: boolean;
};

const ProviderPlaceContext = createContext<ProviderPlaceContextValue | null>(null);

export function useProviderPlace(): ProviderPlaceContextValue {
  const ctx = useContext(ProviderPlaceContext);
  if (!ctx) {
    throw new Error('useProviderPlace must be used within ProviderPlaceProvider');
  }
  return ctx;
}

export function useOptionalProviderPlace(): ProviderPlaceContextValue | null {
  return useContext(ProviderPlaceContext);
}

interface ProviderPlaceProviderProps {
  providerId: number;
  children: React.ReactNode;
}

export const ProviderPlaceProvider: React.FC<ProviderPlaceProviderProps> = ({
  providerId,
  children,
}) => {
  const [selectedPlaceId, setSelectedPlaceIdState] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [pendingPlaceId, setPendingPlaceId] = useState<number | null>(null);

  const { data: placesData, isLoading } = useQuery({
    queryKey: ['provider-items', providerId],
    queryFn: async () => {
      const res = await apiService.getProviderItems(providerId);
      return (res.data.provider_items || []) as ProviderPlaceItem[];
    },
    enabled: providerId > 0,
    staleTime: 60_000,
  });

  const activePlaces = useMemo(
    () => (placesData ?? []).filter((p) => p.is_active !== false),
    [placesData]
  );

  const selectedPlace = useMemo(
    () => activePlaces.find((p) => p.id === selectedPlaceId) ?? null,
    [activePlaces, selectedPlaceId]
  );

  useEffect(() => {
    if (isLoading || providerId <= 0) {
      return;
    }

    if (activePlaces.length === 0) {
      setSelectedPlaceIdState(null);
      setPickerOpen(false);
      setInitialized(true);
      return;
    }

    if (activePlaces.length === 1) {
      const only = activePlaces[0].id;
      setSelectedPlaceIdState(only);
      setStoredProviderPlaceId(providerId, only);
      setPickerOpen(false);
      setInitialized(true);
      return;
    }

    const stored = getStoredProviderPlaceId(providerId);
    if (stored != null && activePlaces.some((p) => p.id === stored)) {
      setSelectedPlaceIdState(stored);
      setPickerOpen(false);
    } else {
      setSelectedPlaceIdState(null);
      setPickerOpen(true);
    }
    setInitialized(true);
  }, [activePlaces, isLoading, providerId]);

  const applyPlaceChange = useCallback(
    (placeId: number) => {
      if (!activePlaces.some((p) => p.id === placeId)) {
        return;
      }
      setSelectedPlaceIdState(placeId);
      setStoredProviderPlaceId(providerId, placeId);
      setPickerOpen(false);
    },
    [activePlaces, providerId]
  );

  const setSelectedPlaceId = useCallback(
    (placeId: number) => {
      if (!activePlaces.some((p) => p.id === placeId)) {
        return;
      }
      if (selectedPlaceId != null && placeId !== selectedPlaceId) {
        setPendingPlaceId(placeId);
        return;
      }
      applyPlaceChange(placeId);
    },
    [activePlaces, selectedPlaceId, applyPlaceChange]
  );

  const pendingPlace = useMemo(
    () => (pendingPlaceId != null ? activePlaces.find((p) => p.id === pendingPlaceId) ?? null : null),
    [activePlaces, pendingPlaceId]
  );

  const value = useMemo(
    (): ProviderPlaceContextValue => ({
      providerId,
      activePlaces,
      selectedPlaceId,
      selectedPlace,
      setSelectedPlaceId,
      hasMultiplePlaces: activePlaces.length > 1,
      isReady:
        initialized &&
        !isLoading &&
        (activePlaces.length <= 1 || selectedPlaceId != null),
    }),
    [
      providerId,
      activePlaces,
      selectedPlaceId,
      selectedPlace,
      setSelectedPlaceId,
      initialized,
      isLoading,
    ]
  );

  return (
    <ProviderPlaceContext.Provider value={value}>
      {children}
      <Dialog open={pickerOpen && activePlaces.length > 1} disableEscapeKeyDown maxWidth="xs" fullWidth>
        <DialogTitle>Où vous trouvez-vous ?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sélectionnez le lieu dans lequel vous travaillez. Ce choix sera mémorisé sur cet appareil.
          </Typography>
          <FormControl fullWidth required>
            <InputLabel>Lieu</InputLabel>
            <Select
              label="Lieu"
              value={selectedPlaceId ?? ''}
              onChange={(e) => setSelectedPlaceId(Number(e.target.value))}
            >
              {activePlaces.map((place) => {
                const color = getProviderPlaceDisplayColor(place);
                return (
                  <MenuItem key={place.id} value={place.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: color,
                          flexShrink: 0,
                        }}
                      />
                      {getPlaceLabel(place)}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingPlaceId != null}
        onClose={() => setPendingPlaceId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Changer de lieu</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Attention, vous allez changer de lieu. Êtes-vous bien au lieu{' '}
            <strong>{pendingPlace ? getPlaceLabel(pendingPlace) : '—'}</strong> ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPendingPlaceId(null)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingPlaceId != null) {
                applyPlaceChange(pendingPlaceId);
              }
              setPendingPlaceId(null);
            }}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </ProviderPlaceContext.Provider>
  );
};
